'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const THEMES = Object.freeze({
  home: { cyan: '#73d4e5', gold: '#dfc16c', ink: '#020914', panel: '#061522' },
  subscribe: { cyan: '#69d9ed', gold: '#dfc16c', ink: '#020a11', panel: '#051521' },
  exchange: { cyan: '#61e8ff', gold: '#e5c85b', ink: '#020912', panel: '#061522' },
  about: { cyan: '#78c9da', gold: '#dfc16c', ink: '#020912', panel: '#061522' },
  academy: { cyan: '#53ecff', gold: '#ffd25f', ink: '#020a12', panel: '#03101c' },
})

export default function PremiumImageLoadFrame({
  children,
  className = '',
  theme = 'home',
  fillParent = false,
  forceFullWidth = false,
  radius = 'inherit',
  resetKey = '',
  reserveWidth = 0,
  reserveHeight = 0,
  style = null,
}) {
  const loadKey = String(resetKey || '')
  const frameRef = useRef(null)
  const [loadState, setLoadState] = useState(() => ({ key: loadKey, ready: false }))
  const loaded = loadState.key === loadKey && loadState.ready
  const palette = THEMES[theme] || THEMES.home
  const reserveRatio = useMemo(() => {
    const width = Number(reserveWidth)
    const height = Number(reserveHeight)
    if (!(width > 0) || !(height > 0)) return ''
    return `${width} / ${height}`
  }, [reserveWidth, reserveHeight])

  const onLoad = useCallback(() => {
    setLoadState((previous) => (
      previous.key === loadKey && previous.ready
        ? previous
        : { key: loadKey, ready: true }
    ))
  }, [loadKey])
  const onError = useCallback(() => {
    setLoadState({ key: loadKey, ready: false })
  }, [loadKey])

  useEffect(() => {
    const image = frameRef.current?.querySelector?.('img')
    if (!image?.complete) return
    if (image.naturalWidth > 0) onLoad()
    else onError()
  }, [loadKey, onError, onLoad])
  const child = typeof children === 'function' ? children({ onLoad, onError, loaded }) : children

  return (
    <span
      ref={frameRef}
      className={`ql7PremiumImageFrame${fillParent ? ' ql7PremiumImageFrame--fillParent' : ''}${forceFullWidth ? ' ql7PremiumImageFrame--forceFullWidth' : ''}${className ? ` ${className}` : ''}`}
      data-ql7-premium-image-preview="1"
      data-ql7-preview-ready={loaded ? '1' : '0'}
      style={{
        '--ql7-preview-cyan': palette.cyan,
        '--ql7-preview-gold': palette.gold,
        '--ql7-preview-ink': palette.ink,
        '--ql7-preview-panel': palette.panel,
        '--ql7-preview-radius': radius,
        ...(!fillParent && !loaded && reserveRatio ? { aspectRatio: reserveRatio } : {}),
        ...(style || {}),
      }}
    >
      {child}

      <span className="ql7PremiumImageFrame__overlay" aria-hidden="true">
        <span className="ql7PremiumImageFrame__core">
          <svg className="ql7PremiumImageFrame__spinner" viewBox="0 0 120 120" focusable="false" aria-hidden="true">
            <circle className="ql7PremiumImageFrame__track" cx="60" cy="60" r="49" pathLength="100" />
            <circle className="ql7PremiumImageFrame__arc" cx="60" cy="60" r="49" pathLength="100" />
            <circle className="ql7PremiumImageFrame__inner" cx="60" cy="60" r="39" />
          </svg>

          <svg className="ql7PremiumImageFrame__wordmark" viewBox="0 0 104 28" focusable="false" aria-hidden="true">
            <path d="M5 4 H31 M73 4 H99" />
            <path d="M5 24 H23 M81 24 H99" />
            <text x="52" y="17.3" textAnchor="middle">PREVIEW...</text>
          </svg>
        </span>
      </span>

      <style jsx>{`
        .ql7PremiumImageFrame {
          position: relative;
          display: block;
          width: 100%;
          min-width: 0;
          overflow: hidden;
          border-radius: var(--ql7-preview-radius);
          background: var(--ql7-preview-ink);
          isolation: isolate;
        }

        .ql7PremiumImageFrame--fillParent {
          position: absolute;
          inset: 0;
          width: auto;
          height: auto;
        }

        .ql7PremiumImageFrame--forceFullWidth {
          width: 100% !important;
          max-width: none !important;
          min-width: 0 !important;
          align-self: stretch;
          justify-self: stretch;
        }

        :global(.ql7PremiumImageFrame--forceFullWidth > img) {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          height: auto !important;
          max-height: none !important;
          object-fit: contain;
        }

        .ql7PremiumImageFrame__overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          display: grid;
          place-items: center;
          overflow: hidden;
          pointer-events: none;
          border-radius: inherit;
          opacity: 1;
          visibility: visible;
          background:
            linear-gradient(135deg, rgba(255,255,255,.018), transparent 28%, transparent 72%, rgba(255,255,255,.012)),
            linear-gradient(160deg, var(--ql7-preview-panel), var(--ql7-preview-ink) 72%);
          box-shadow:
            inset 0 0 0 1px rgba(255,255,255,.045),
            inset 0 0 42px rgba(0,0,0,.24);
          transition: opacity .22s ease, visibility 0s linear 0s;
        }

        .ql7PremiumImageFrame[data-ql7-preview-ready='1'] .ql7PremiumImageFrame__overlay {
          opacity: 0;
          visibility: hidden;
          transition: opacity .22s ease, visibility 0s linear .22s;
        }

        .ql7PremiumImageFrame__core {
          position: relative;
          display: grid;
          place-items: center;
          width: clamp(88px, 18vw, 146px);
          aspect-ratio: 1;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255,255,255,.028) 0 38%, transparent 39% 100%);
          box-shadow:
            0 0 0 1px rgba(255,255,255,.028),
            0 0 26px rgba(0,0,0,.25);
        }

        .ql7PremiumImageFrame__spinner {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: visible;
          animation: ql7PremiumPreviewSpin 1.6s linear infinite;
          will-change: transform;
        }

        .ql7PremiumImageFrame__track,
        .ql7PremiumImageFrame__arc,
        .ql7PremiumImageFrame__inner {
          fill: none;
          vector-effect: non-scaling-stroke;
        }

        .ql7PremiumImageFrame__track {
          stroke: rgba(255,255,255,.095);
          stroke-width: 1.3;
        }

        .ql7PremiumImageFrame__arc {
          stroke: var(--ql7-preview-cyan);
          stroke-width: 2.2;
          stroke-linecap: round;
          stroke-dasharray: 24 76;
          opacity: .92;
        }

        .ql7PremiumImageFrame__inner {
          stroke: var(--ql7-preview-gold);
          stroke-width: .9;
          stroke-dasharray: 2 6;
          opacity: .58;
        }

        .ql7PremiumImageFrame__wordmark {
          position: relative;
          z-index: 2;
          width: 72%;
          height: auto;
          overflow: visible;
          color: var(--ql7-preview-gold);
        }

        .ql7PremiumImageFrame__wordmark path {
          fill: none;
          stroke: var(--ql7-preview-cyan);
          stroke-width: .7;
          opacity: .6;
          vector-effect: non-scaling-stroke;
        }

        .ql7PremiumImageFrame__wordmark text {
          fill: currentColor;
          stroke: none;
          font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 8.1px;
          font-weight: 900;
          letter-spacing: 1.25px;
        }

        .ql7PremiumImageFrame[data-ql7-preview-ready='1'] .ql7PremiumImageFrame__spinner {
          animation-play-state: paused;
        }

        @keyframes ql7PremiumPreviewSpin {
          to { transform: rotate(360deg); }
        }

        @media (prefers-reduced-motion: reduce) {
          .ql7PremiumImageFrame__spinner { animation: none; }
          .ql7PremiumImageFrame__overlay { transition: none; }
        }
      `}</style>
    </span>
  )
}
